<script setup lang="ts">
import type { CookieCategoryDefinition, CookieConsentCategory } from '#shared/types/cookie-consent'
import { computed, ref, watch } from 'vue'

const {
  categoryDefinitions,
  consentState,
  isBannerVisible,
  isSettingsOpen,
  acceptAll,
  rejectNonEssential,
  saveSelected,
  openSettings,
  closeSettings,
} = useCookieConsent()

const selectedCategories = ref<CookieConsentCategory[]>([])

const syncSelectionFromState = () => {
  selectedCategories.value = categoryDefinitions
    .filter((definition: CookieCategoryDefinition) => consentState.value.categories[definition.key])
    .map((definition: CookieCategoryDefinition) => definition.key)
}

watch(isSettingsOpen, isOpen => {
  if (isOpen)
    syncSelectionFromState()
})

const hasChanges = computed(() => {
  const selection = new Set(selectedCategories.value)

  return categoryDefinitions.some((definition: CookieCategoryDefinition) => {
    const nextValue = definition.required ? true : selection.has(definition.key)

    return consentState.value.categories[definition.key] !== nextValue
  })
})

const toggleCategory = (category: CookieConsentCategory, enabled: boolean) => {
  const definition = categoryDefinitions.find((item: CookieCategoryDefinition) => item.key === category)
  if (definition?.required)
    return

  const next = new Set(selectedCategories.value)
  if (enabled)
    next.add(category)
  else
    next.delete(category)

  selectedCategories.value = Array.from(next)
}

const handleSave = () => {
  saveSelected(selectedCategories.value)
}

const showSettingsLauncher = computed(() => consentState.value.hasInteracted)
</script>

<template>
  <ClientOnly>
    <div class="cookie-consent">
      <Transition name="cookie-consent-slide">
        <VSheet
          v-if="isBannerVisible"
          class="cookie-consent__banner"
          role="dialog"
          aria-live="polite"
          aria-labelledby="cookie-consent-title"
          aria-describedby="cookie-consent-description"
          elevation="12"
        >
          <div class="cookie-consent__text">
            <h2 id="cookie-consent-title" class="sr-only">
              Cookie instellingen
            </h2>
            <p id="cookie-consent-description" class="cookie-consent__description">
              We gebruiken cookies om de site goed te laten werken en om je voorkeuren te onthouden.
              <br>
              Meer info in ons
              <NuxtLink
                to="/legal/cookie-policy"
                class="cookie-consent__link"
              >
                cookiebeleid
              </NuxtLink>
              en onze
              <NuxtLink
                to="/legal/privacy-gdpr"
                class="cookie-consent__link"
              >
                privacyverklaring
              </NuxtLink>.
            </p>
          </div>

          <div class="cookie-consent__actions">
            <VBtn
              variant="flat"
              color="primary"
              @click="acceptAll"
            >
              Alles accepteren
            </VBtn>
            <VBtn
              variant="flat"
              color="primary"
              @click="rejectNonEssential"
            >
              Alleen noodzakelijke
            </VBtn>
            <VBtn
              icon
              variant="text"
              size="small"
              class="cookie-consent__close"
              aria-label="Sluiten en alleen noodzakelijke cookies accepteren"
              @click="rejectNonEssential"
            >
              <VIcon
                icon="bx-x"
                size="20"
              />
            </VBtn>
          </div>
        </VSheet>
      </Transition>

      <Transition name="cookie-settings-fade">
        <VBtn
          v-if="showSettingsLauncher"
          class="cookie-consent__launcher"
          icon
          variant="tonal"
          color="primary"
          size="large"
          @click="openSettings"
        >
          <VIcon
            icon="bx-cookie"
            size="24"
          />
          <VTooltip
            activator="parent"
            location="top"
          >
            Cookie-instellingen
          </VTooltip>
        </VBtn>
      </Transition>

      <VDialog
        v-model="isSettingsOpen"
        max-width="640"
        scrollable
      >
        <VCard>
          <VCardTitle class="text-h4 font-weight-bold pb-0">
            Cookievoorkeuren
          </VCardTitle>
          <VCardSubtitle class="pt-1">
            Kies welke cookies je toestaat. Noodzakelijke cookies staan altijd aan.
          </VCardSubtitle>

          <VCardText class="cookie-preference__list">
            <div
              v-for="definition in categoryDefinitions"
              :key="definition.key"
              class="cookie-preference__item"
            >
              <div class="cookie-preference__info">
                <p class="text-subtitle-1 mb-1 font-weight-medium">
                  {{ definition.label }}
                </p>
                <p class="text-body-2 mb-0 text-medium-emphasis">
                  {{ definition.description }}
                </p>
              </div>
              <VSwitch
                :model-value="!!(definition.required || selectedCategories.includes(definition.key))"
                :disabled="definition.required"
                inset
                hide-details
                color="primary"
                class="flex-shrink-0"
                @update:model-value="(value: boolean | null) => toggleCategory(definition.key, !!value)"
              />
            </div>
          </VCardText>

          <VDivider />

          <VCardActions class="flex-wrap gap-3">
            <VBtn
              variant="text"
              @click="closeSettings"
            >
              Sluiten
            </VBtn>
            <VSpacer />
            <VBtn
              variant="outlined"
              color="primary"
              @click="rejectNonEssential"
            >
              Alleen noodzakelijke
            </VBtn>
            <VBtn
              color="primary"
              :disabled="!hasChanges"
              @click="handleSave"
            >
              Opslaan
            </VBtn>
            <VBtn
              variant="tonal"
              color="primary"
              @click="acceptAll"
            >
              Alles accepteren
            </VBtn>
          </VCardActions>
        </VCard>
      </VDialog>
    </div>
  </ClientOnly>
</template>

<style scoped>
.cookie-consent {
  position: relative;
  z-index: 1000;
}

.cookie-consent__banner {
  position: fixed;
  z-index: 960;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
  border-radius: 0;
  border-block-start: 3px solid rgb(var(--v-theme-primary));
  gap: 2rem;
  inline-size: 100%;
  inset-block-end: 0;
  inset-inline-start: 0;
  padding-block: 1.25rem;
  padding-inline: 3rem;
  transform: none;
}

.cookie-consent__text {
  flex: 1;
  max-inline-size: none;
}

.cookie-consent__description {
  color: rgb(var(--v-theme-on-surface));
  font-size: 0.95rem;
  line-height: 1.6;
  margin-block-end: 0;
}

.cookie-consent__link {
  color: rgb(var(--v-theme-primary));
  text-decoration: underline;
  text-underline-offset: 2px;
}

.cookie-consent__link:hover {
  opacity: 0.8;
}

.cookie-consent__actions {
  display: flex;
  flex-direction: row;
  flex-shrink: 0;
  align-items: center;
  gap: 0.75rem;
}

.cookie-consent__close {
  margin-inline-start: 0.5rem;
}

.cookie-consent__launcher {
  position: fixed;
  z-index: 999;
  inset-block-end: calc(4.8% + 0px);
  inset-inline-start: 25px;
}

.cookie-preference__title {
  display: flex;
  align-items: center;
  gap: 1rem;
  inline-size: 100%;
}

.cookie-preference__list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.cookie-preference__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
  gap: 1rem;
  padding-block: 0.75rem;
  padding-inline: 1rem;
}

.cookie-preference__info {
  flex: 1;
}

@media (max-width: 768px) {
  .cookie-consent__banner {
    flex-flow: row wrap;
    gap: 1rem;
    padding-block: 1rem;
    padding-inline: 1rem;
  }

  .cookie-consent__text {
    inline-size: 100%;
  }

  .cookie-consent__description {
    font-size: 1rem !important;
    line-height: 1.3 !important;
    text-align: justify;
  }

  .cookie-consent__actions {
    flex-flow: row wrap;
    gap: 0.5rem;
    inline-size: 100%;
  }

  .cookie-consent__actions .v-btn {
    block-size: 32px !important;
    font-size: 1rem !important;
    padding-inline: 12px !important;
  }

  .cookie-consent__launcher {
    display: none;
  }
}

.cookie-consent-slide-enter-active,
.cookie-consent-slide-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.cookie-consent-slide-enter-from,
.cookie-consent-slide-leave-to {
  opacity: 0;
  transform: translateY(100%);
}

.cookie-settings-fade-enter-active,
.cookie-settings-fade-leave-active {
  transition: opacity 0.2s ease;
}

.cookie-settings-fade-enter-from,
.cookie-settings-fade-leave-to {
  opacity: 0;
}
</style>
